import { sleep } from 'src/utils/sleep';

import { authClient } from './auth';

class HttpError extends Error {
  public json: unknown;
  public status: number;
  public url: string;

  constructor(json: unknown, status: number, url: string) {
    super(`Received HTTP ${status} response`);

    this.name = 'HttpError';

    this.json = json;
    this.status = status;
    this.url = url;
  }
}

/**
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/AbortController/abort#examples}
 */
export const isAbortError = (err: unknown) =>
  err instanceof DOMException && err.name === 'AbortError';

export const isHttpError = (err: unknown, status: number): err is HttpError =>
  err instanceof HttpError && err.status === status;

interface Props {
  attempts: number;
  request: unknown;
  timeout: number;
  url: string;
  keepalive?: boolean;
}

export const postJson = async <T>(props: Props): Promise<T> => {
  const { attempts, request, timeout: ms, url, keepalive } = props;

  const token = await authClient.accessToken();

  // Failsafe; only reachable if accessToken() fails its login redirect.
  // istanbul ignore next
  if (!token) {
    throw new Error('User is unauthenticated');
  }

  const controller = new AbortController();

  const timeout = sleep(() => {
    controller.abort();
  }, ms);

  let response: Response;

  try {
    response = await window.fetch(url, {
      body: JSON.stringify(request),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      signal: controller.signal,
      keepalive,
    });
  } catch (err: unknown) {
    if (isAbortError(err) && attempts > 1) {
      return postJson({ ...props, attempts: attempts - 1 });
    }

    throw err;
  } finally {
    clearTimeout(timeout);
  }

  if (response.status >= 500 && attempts > 1) {
    return postJson({ ...props, attempts: attempts - 1 });
  }

  if (!response.ok) {
    const json = await response.json().catch(() => undefined);

    throw new HttpError(json, response.status, url);
  }

  try {
    const json = await response.json();
    return json as unknown as T;
  } catch {
    // Some endpoints don't return nor expect a json body
    return undefined as unknown as T;
  }
};
